import type React from "react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
} from "@planta-v2/pds-ui/underline-tabs";
import { useDirectoryController } from "../controllers/useDirectoryController";
import {
  DirectoryTab,
  DirectoryTabSchema,
  type DirectoryTabType,
} from "../../domain/types/tab.types";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import {
  ConnectionRequestIdPublic,
  EventIdPublicOrSlug,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { TopPick } from "@frontend/shared/domain/types/top-pick.types";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { i18n } from "@frontend/i18n";
import { EventHeader } from "@/shared/presentation/components/EventHeader";
import { useEventTheme } from "@/shared/presentation/hooks/useEventTheme";
import { DirectorySearchBar } from "../components/directory/DirectorySearchBar";
import { DirectorySearchGroup } from "../components/directory/DirectorySearchGroup";
import { TopPicksBanner } from "../components/directory/TopPicksBanner";
import { SuggestionsRecalculatingNotice } from "../components/directory/SuggestionsRecalculatingNotice";
import { TopPicksInfoBanner } from "../components/directory/TopPicksInfoBanner";
import { ProfileListItem } from "../components/directory/ProfileListItem";
import { TopPicksListItem } from "@frontend/shared/presentation/components/participant-profile/TopPicksListItem";
import { ProfileList } from "../components/directory/ProfileList";
import { DirectoryLetterHeading } from "../components/directory/DirectoryLetterHeading";
import { DirectoryIndexRail } from "../components/directory/DirectoryIndexRail";
import {
  DIRECTORY_INDEX_LETTERS,
  resolveDirectoryLetter,
} from "../../domain/services/directory-letter-index.service";
import { GenerateMoreButton } from "../components/directory/GenerateMoreButton";
import { IsolatedTopPicksGate } from "../components/directory/IsolatedTopPicksGate";
import { RequestsInfoBanner } from "../components/requests/RequestsInfoBanner";
import { ConnectionRequestCardContainer } from "../components/requests/ConnectionRequestCardContainer";
import { SentRequestsView } from "../components/requests/SentRequestsView";
import { DirectoryPageSkeleton } from "../components/state/DirectoryPageSkeleton";
import { DirectoryListSkeleton } from "../components/state/DirectoryListSkeleton";
import { DirectoryEmptyState } from "../components/state/DirectoryEmptyState";
import { DirectoryErrorState } from "../components/state/DirectoryErrorState";
import { useScrollRestoration } from "@/shared/presentation/hooks/useScrollRestoration";
import {
  SearchGroup,
  type SearchGroupType,
} from "../../domain/types/search-group.types";

// Below a page of guests the list is short enough to flick through, and a rail
// of mostly-absent letters reads as broken rather than as a shortcut.
const INDEX_RAIL_MIN_ENTRIES = 25;

interface DirectoryRow {
  readonly entry: DirectoryCard;
  readonly headingLetter: string | null;
}

// Search narrows the set server-side and gates the background prefetch off, so
// the rail would sit almost entirely dim and jump nowhere useful.
function shouldShowIndexRail(searchQuery: string, entryCount: number): boolean {
  return searchQuery === "" && entryCount >= INDEX_RAIL_MIN_ENTRIES;
}

function shouldTuckForSearch(
  activeTab: DirectoryTabType,
  isSearchFocused: boolean,
  searchQuery: string,
): boolean {
  return (
    activeTab === DirectoryTab.ALL && (isSearchFocused || searchQuery !== "")
  );
}

// Search results come back ranked by relevance, not by name, so letter headings
// would split them into runs that mean nothing.
function buildDirectoryRows(
  entries: readonly DirectoryCard[],
  searchQuery: string,
): readonly DirectoryRow[] {
  if (searchQuery !== "") {
    return entries.map((entry) => ({ entry, headingLetter: null }));
  }
  const built: DirectoryRow[] = [];
  let previous: string | null = null;
  for (const entry of entries) {
    const letter = resolveDirectoryLetter(entry.lastName);
    built.push({ entry, headingLetter: letter === previous ? null : letter });
    previous = letter;
  }
  return built;
}

function readingProfilesNote(count: number | undefined): string | undefined {
  if (count === undefined) return undefined;
  if (count === 1) return i18n.t("user.directory.search.groups.readingOne");
  return i18n.t("user.directory.search.groups.reading", { count });
}

// Rect deltas, not scrollIntoView: that walks every ancestor scrollport, including the
// visual viewport the iOS keyboard shifts.
function scrollContainerToLetter(
  container: HTMLDivElement,
  letter: string,
  loadedLetters: ReadonlySet<string>,
): void {
  const targetIndex = DIRECTORY_INDEX_LETTERS.indexOf(letter);
  if (targetIndex < 0) return;

  // Pages are still arriving mid-drag, so the target heading may not exist yet.
  // Stopping at the furthest loaded letter still pulls the next page in. Resolved
  // against the loaded set rather than the DOM: a bare attribute selector has no
  // fast path, so probing each candidate would rewalk the whole list per move.
  for (let index = targetIndex; index >= 0; index -= 1) {
    const candidate = DIRECTORY_INDEX_LETTERS[index];
    if (candidate === undefined || !loadedLetters.has(candidate)) continue;

    const heading = container.querySelector(
      `[data-directory-letter="${candidate}"]`,
    );
    if (heading === null) return;

    container.scrollTop +=
      heading.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    return;
  }
}

export function DirectoryPage(): React.JSX.Element {
  const { state, actions } = useDirectoryController();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topPicksScrollRef = useRef<HTMLDivElement>(null);
  const requestsScrollRef = useRef<HTMLDivElement>(null);
  const connectedScrollRef = useRef<HTMLDivElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const showingSkeleton =
    state.isLoading &&
    state.allEntries === undefined &&
    state.filteredEntries.length === 0;
  const tabsVisible =
    !showingSkeleton && state.error === null && !state.showSentRequests;
  const isSearchTucked = shouldTuckForSearch(
    state.activeTab,
    isSearchFocused,
    state.searchQuery,
  );
  const scrollKeyPrefix = `directory:${state.eventIdPublic}`;
  useScrollRestoration(
    scrollContainerRef,
    `${scrollKeyPrefix}:${DirectoryTab.ALL}`,
    tabsVisible && state.activeTab === DirectoryTab.ALL,
  );
  useScrollRestoration(
    topPicksScrollRef,
    `${scrollKeyPrefix}:${DirectoryTab.TOP_PICKS}`,
    tabsVisible && state.activeTab === DirectoryTab.TOP_PICKS,
  );
  useScrollRestoration(
    requestsScrollRef,
    `${scrollKeyPrefix}:${DirectoryTab.REQUESTS}`,
    tabsVisible && state.activeTab === DirectoryTab.REQUESTS,
  );
  useScrollRestoration(
    connectedScrollRef,
    `${scrollKeyPrefix}:${DirectoryTab.CONNECTED}`,
    tabsVisible && state.activeTab === DirectoryTab.CONNECTED,
  );

  const rows = useMemo(
    () => buildDirectoryRows(state.filteredEntries, state.searchQuery),
    [state.filteredEntries, state.searchQuery],
  );

  // Every present letter opens exactly one run, so the headings already name the
  // whole set the rail can reach.
  const availableLetters = useMemo(
    () =>
      new Set(
        rows
          .map((row) => row.headingLetter)
          .filter((letter): letter is string => letter !== null),
      ),
    [rows],
  );

  const isIndexRailVisible = shouldShowIndexRail(
    state.searchQuery,
    state.filteredEntries.length,
  );

  const handleIndexLetterChange = useCallback(
    (letter: string): void => {
      const container = scrollContainerRef.current;
      if (container === null) return;
      scrollContainerToLetter(container, letter, availableLetters);
    },
    [availableLetters],
  );

  useEventTheme(state.themeMode);

  if (showingSkeleton) {
    return (
      <DirectoryPageSkeleton
        showSearch={state.activeTab === DirectoryTab.ALL}
      />
    );
  }

  if (state.error !== null) {
    return (
      <div className="flex min-h-dvh flex-col justify-center bg-background">
        <DirectoryErrorState
          onRetry={actions.retry}
          error={state.error}
          isRetrying={state.isRetrying}
        />
      </div>
    );
  }

  if (state.showSentRequests) {
    return (
      <SentRequestsView
        requests={state.sentRequests}
        isLoading={state.isSentRequestsLoading}
        onBack={(): void => {
          actions.setShowSentRequests(false);
        }}
        onViewProfile={actions.handleProfileClick}
      />
    );
  }

  return (
    <UnderlineTabs
      value={state.activeTab}
      onValueChange={(value): void => {
        const parsedTab = DirectoryTabSchema.safeParse(value);
        if (parsedTab.success) {
          actions.setActiveTab(parsedTab.data);
        }
      }}
      className="flex h-dvh flex-col bg-background"
    >
      <span
        hidden
        data-prefetch-complete={state.prefetch.isComplete ? "true" : "false"}
      />
      {/* Fixed Header Section - Header + Tabs + Search Bar */}
      <div className="flex-shrink-0 bg-background">
        <div
          className="grid grid-rows-[1fr] inert:grid-rows-[0fr] inert:opacity-0 motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-200 motion-safe:ease-out"
          inert={isSearchTucked}
        >
          <div className="min-h-0 overflow-hidden">
            <EventHeader
              eventName={
                state.event !== undefined
                  ? LocalizedTextUtils.resolve(
                      state.event.name,
                      i18n.getLocale(),
                    )
                  : i18n.t("user.directory.defaultEventName")
              }
              logoUrl={state.event?.logoUrl}
              hasNotification={state.requestCount > 0}
              onUserIconClick={actions.handleEditOwnProfile}
              viewerPhotoUrl={state.viewerPhotoUrl}
              viewerDisplayName={state.viewerDisplayName}
            />
            <UnderlineTabsList testId="directory-tabs-list">
              {state.isAiRecommendationsActive && (
                <UnderlineTabsTrigger
                  value={DirectoryTab.TOP_PICKS}
                  testId="directory-tab-top-picks"
                >
                  {i18n.t("user.directory.tabs.topPicks")}
                </UnderlineTabsTrigger>
              )}
              <UnderlineTabsTrigger
                value={DirectoryTab.REQUESTS}
                badge={state.requestCount > 0 ? state.requestCount : undefined}
                testId="directory-tab-requests"
              >
                {i18n.t("user.directory.tabs.requests")}
              </UnderlineTabsTrigger>
              <UnderlineTabsTrigger
                value={DirectoryTab.CONNECTED}
                testId="directory-tab-connected"
              >
                {i18n.t("user.directory.tabs.connected")}
              </UnderlineTabsTrigger>
              <UnderlineTabsTrigger
                value={DirectoryTab.ALL}
                testId="directory-tab-all"
              >
                {i18n.t("user.directory.tabs.all")}
              </UnderlineTabsTrigger>
            </UnderlineTabsList>
          </div>
        </div>
        {/* Search Bar - Fixed above scroll area to avoid mobile sticky wobble.
            Search is All-only, deliberately: All is the whole guest list, while
            Suggestions, Requests and Connected are short curated sets you read
            rather than search. DirectoryPageSkeleton mirrors this so the field
            does not appear during load and then vanish. */}
        {state.activeTab === DirectoryTab.ALL && (
          <div className="px-4 pt-2 pb-4">
            <DirectorySearchBar
              value={state.searchQuery}
              onChange={actions.setSearchQuery}
              onBlur={actions.syncSearchQueryToUrl}
              totalCount={state.totalEntriesCount}
              isSearching={state.isSearchInFlight}
              onFocusChange={setIsSearchFocused}
              isCancelShown={isSearchTucked}
            />
          </div>
        )}
      </div>

      {/* All Tab — a row, not an overlay: the rail takes its own column so it
          never covers the scroller's scrollbar or the rows' chevron. */}
      <UnderlineTabsContent
        value={DirectoryTab.ALL}
        className="flex min-h-0 flex-1 flex-row"
      >
        <div
          ref={scrollContainerRef}
          // Desktop-only in effect — mobile scrollbars already overlay and fade.
          // The rail is the position indicator here, as iOS Contacts does it.
          className="min-w-0 flex-1 overflow-y-auto scrollbar-hide"
          data-testid="directory-scroll-container-all"
        >
          <div className="flex min-h-full flex-col px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <AllTabContent
              rows={rows}
              isBannerDismissed={state.isBannerDismissed}
              isSearchTucked={isSearchTucked}
              isAiRecommendationsActive={state.isAiRecommendationsActive}
              isRecalculationPending={state.isRecalculationPending}
              onDismissBanner={actions.dismissBanner}
              onGoToTopPicks={(): void => {
                actions.setActiveTab(DirectoryTab.TOP_PICKS);
              }}
              onProfileClick={actions.handleProfileClick}
              totalCountForBanner={state.totalEntriesCountForBanner}
              currentUserParticipantIdPublic={
                state.currentUserParticipantIdPublic
              }
              searchQuery={state.searchQuery}
              expandedSearchGroups={state.expandedSearchGroups}
              onExpandSearchGroup={actions.expandSearchGroup}
              isSearchPending={state.isSearchPending}
              isNameGroupShown={state.isNameGroupShown}
              isMeaningGroupShown={state.isMeaningGroupShown}
              nameEntries={state.filteredEntries}
              meaningEntries={state.meaningEntries}
              isMeaningSearchPending={state.isMeaningSearchPending}
              hasNextPage={state.hasNextPage}
              isFetchingNextPage={state.isFetchingNextPage}
              onFetchNextPage={actions.fetchNextPage}
              scrollContainerRef={scrollContainerRef}
            />
          </div>
        </div>
        {isIndexRailVisible && (
          <DirectoryIndexRail
            availableLetters={availableLetters}
            onLetterChange={handleIndexLetterChange}
          />
        )}
      </UnderlineTabsContent>

      {/* Top Picks Tab */}
      {state.isAiRecommendationsActive && (
        <UnderlineTabsContent
          ref={topPicksScrollRef}
          value={DirectoryTab.TOP_PICKS}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        >
          <TopPicksTabContent
            picks={state.topPicks}
            isLoading={state.isTopPicksLoading}
            isRecalculationPending={state.isRecalculationPending}
            isGeneratingMore={state.isGeneratingMore}
            hasMorePicks={state.hasMoreTopPicks}
            onGenerateMore={actions.handleGenerateMore}
            onProfileClick={actions.handleTopPickClick}
            isIsolatedGateActive={state.isIsolatedGateActive}
            isOptingIn={state.isOptingIn}
            optInError={state.optInError}
            onOptInRemember={actions.onOptInRemember}
            onBrowseAll={() => {
              actions.setActiveTab(DirectoryTab.ALL);
            }}
          />
        </UnderlineTabsContent>
      )}

      {/* Requests Tab */}
      <UnderlineTabsContent
        ref={requestsScrollRef}
        value={DirectoryTab.REQUESTS}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      >
        <RequestsTabContent
          requests={state.connectionRequests}
          isLoading={state.isRequestsLoading}
          eventIdPublicOrSlug={EventIdPublicOrSlug.unsafeCreate(
            state.eventIdPublic,
          )}
          onViewProfile={actions.handleProfileClick}
          onBrowseAll={(): void => {
            actions.setActiveTab(DirectoryTab.ALL);
          }}
          onShowSentRequests={(): void => {
            actions.setShowSentRequests(true);
          }}
        />
      </UnderlineTabsContent>

      {/* Connected Tab */}
      <UnderlineTabsContent
        ref={connectedScrollRef}
        value={DirectoryTab.CONNECTED}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      >
        <ConnectedTabContent
          profiles={state.connectedProfiles}
          isLoading={state.isConnectedLoading}
          onProfileClick={actions.handleProfileClick}
          onBrowseAll={(): void => {
            actions.setActiveTab(DirectoryTab.ALL);
          }}
        />
      </UnderlineTabsContent>
    </UnderlineTabs>
  );
}

interface AllTabContentProps {
  readonly rows: readonly DirectoryRow[];
  readonly isBannerDismissed: boolean;
  readonly isSearchTucked: boolean;
  readonly isAiRecommendationsActive: boolean;
  readonly isRecalculationPending: boolean;
  readonly onDismissBanner: () => void;
  readonly onGoToTopPicks: () => void;
  readonly onProfileClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;

  readonly totalCountForBanner: number | undefined;
  readonly currentUserParticipantIdPublic: string | null;
  readonly searchQuery: string;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onFetchNextPage: () => void;

  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  readonly expandedSearchGroups: readonly SearchGroupType[];
  readonly onExpandSearchGroup: (group: SearchGroupType) => void;
  readonly isSearchPending: boolean;
  readonly isNameGroupShown: boolean;
  readonly isMeaningGroupShown: boolean;
  readonly nameEntries: readonly DirectoryCard[];
  readonly meaningEntries: readonly DirectoryCard[];
  readonly isMeaningSearchPending: boolean;
}

function AllTabContent({
  rows,
  isBannerDismissed,
  isSearchTucked,
  isAiRecommendationsActive,
  isRecalculationPending,
  onDismissBanner,
  onGoToTopPicks,
  onProfileClick,
  totalCountForBanner,
  currentUserParticipantIdPublic,
  searchQuery,
  expandedSearchGroups,
  onExpandSearchGroup,
  isSearchPending,
  isNameGroupShown,
  isMeaningGroupShown,
  nameEntries,
  meaningEntries,
  isMeaningSearchPending,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  scrollContainerRef,
}: AllTabContentProps): React.JSX.Element {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const shouldUseInfiniteScroll = hasNextPage;
  const showsSearchGroups = isNameGroupShown || isMeaningGroupShown;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (sentinel === null || !shouldUseInfiniteScroll) return;

    const observer = new IntersectionObserver(
      (observerEntries): void => {
        const entry = observerEntries[0];

        if (
          entry !== undefined &&
          entry.isIntersecting &&
          !isFetchingNextPage
        ) {
          onFetchNextPage();
        }
      },
      { root: scrollContainerRef.current, rootMargin: "200px" }, // Observe against scrollable container, not viewport
    );

    observer.observe(sentinel);
    return (): void => {
      observer.disconnect();
    };
  }, [
    shouldUseInfiniteScroll,
    hasNextPage,
    isFetchingNextPage,
    onFetchNextPage,
    scrollContainerRef,
  ]);

  return (
    <>
      {/* A run in flight withdraws the invitation: the deck this opens is about
          to drop picks, and only the tab can say so. */}
      {!isBannerDismissed &&
        !isSearchTucked &&
        isAiRecommendationsActive &&
        !isRecalculationPending && (
          <div className="pb-4">
            <TopPicksBanner
              onDismiss={onDismissBanner}
              onClick={onGoToTopPicks}
              totalProfilesCount={totalCountForBanner}
            />
          </div>
        )}

      {/* Entries List — the previous query's results stay loaded while a new
          one is in flight, so they are hidden rather than shown as its answer. */}
      {showsSearchGroups ? (
        <>
          {isNameGroupShown && (
            <DirectorySearchGroup
              testId="directory-search-group-names"
              label={i18n.t("user.directory.search.groups.names")}
              entries={nameEntries}
              isLoading={isSearchPending}
              searchQuery={searchQuery}
              isExpanded={expandedSearchGroups.includes(SearchGroup.NAMES)}
              onExpand={(): void => {
                onExpandSearchGroup(SearchGroup.NAMES);
              }}
              isQueryMarked
              currentUserParticipantIdPublic={currentUserParticipantIdPublic}
              onProfileClick={onProfileClick}
            />
          )}
          {isMeaningGroupShown && (
            <DirectorySearchGroup
              testId="directory-search-group-meaning"
              label={i18n.t("user.directory.search.groups.meaning")}
              entries={meaningEntries}
              isLoading={isMeaningSearchPending}
              loadingNote={readingProfilesNote(totalCountForBanner)}
              searchQuery={searchQuery}
              isExpanded={expandedSearchGroups.includes(SearchGroup.MEANING)}
              onExpand={(): void => {
                onExpandSearchGroup(SearchGroup.MEANING);
              }}
              isBioShown
              currentUserParticipantIdPublic={currentUserParticipantIdPublic}
              onProfileClick={onProfileClick}
            />
          )}
        </>
      ) : rows.length === 0 ? (
        <DirectoryEmptyState tab={DirectoryTab.ALL} searchQuery={searchQuery} />
      ) : (
        <ProfileList>
          {rows.map(({ entry, headingLetter }) => (
            <Fragment
              key={EventParticipantIdPublic.unwrap(entry.participantIdPublic)}
            >
              {headingLetter !== null && (
                <DirectoryLetterHeading letter={headingLetter} />
              )}
              <ProfileListItem
                entry={entry}
                onClick={onProfileClick}
                isViewer={
                  EventParticipantIdPublic.unwrap(entry.participantIdPublic) ===
                  currentUserParticipantIdPublic
                }
              />
            </Fragment>
          ))}
          {/* Sentinel element for infinite scroll */}
          <div ref={sentinelRef} className="h-1" />
          {/* Loading indicator */}
          {isFetchingNextPage && (
            <div className="py-4 text-center text-muted-foreground text-sm">
              {i18n.t("user.directory.loadingMore")}
            </div>
          )}
        </ProfileList>
      )}
    </>
  );
}

interface TopPicksTabContentProps {
  readonly picks: readonly TopPick[] | undefined;
  readonly isLoading: boolean;
  readonly isRecalculationPending: boolean;
  readonly isGeneratingMore: boolean;
  readonly hasMorePicks: boolean;
  readonly onGenerateMore: () => Promise<void>;
  readonly onProfileClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly isIsolatedGateActive: boolean;
  readonly isOptingIn: boolean;
  readonly optInError: Error | null;
  readonly onOptInRemember: () => Promise<void>;
  readonly onBrowseAll: () => void;
}

function TopPicksTabContent({
  picks,
  isLoading,
  isRecalculationPending,
  isGeneratingMore,
  hasMorePicks,
  onGenerateMore,
  onProfileClick,
  isIsolatedGateActive,
  isOptingIn,
  optInError,
  onOptInRemember,
  onBrowseAll,
}: TopPicksTabContentProps): React.JSX.Element {
  if (isLoading) {
    return <DirectoryListSkeleton />;
  }

  // A run in flight owns the slot; nothing else claims it now that intake is gone.
  const banner = isRecalculationPending ? (
    <div className="pt-3">
      <SuggestionsRecalculatingNotice />
    </div>
  ) : null;

  if (picks === undefined || picks.length === 0) {
    return (
      <div className="flex flex-col">
        {banner}
        <DirectoryEmptyState
          tab={DirectoryTab.TOP_PICKS}
          onBrowseAll={onBrowseAll}
        />
      </div>
    );
  }

  if (isIsolatedGateActive) {
    const visiblePicks = picks.slice(0, 2);
    return (
      <div className="flex flex-col">
        {banner}
        <TopPicksInfoBanner recalculating={isRecalculationPending} />
        <div className="flex flex-col">
          {visiblePicks.map((pick) => (
            <TopPicksListItem
              key={EventParticipantIdPublic.unwrap(
                pick.entry.participantIdPublic,
              )}
              pick={pick}
              onClick={onProfileClick}
            />
          ))}
          <IsolatedTopPicksGate
            thirdPick={picks[2]}
            isOptingIn={isOptingIn}
            onOptIn={(): void => {
              void onOptInRemember();
            }}
            errorMessage={
              optInError !== null
                ? i18n.t("user.directory.topPicks.isolatedGate.error")
                : null
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {banner}
      <TopPicksInfoBanner recalculating={isRecalculationPending} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          {picks.map((pick) => (
            <TopPicksListItem
              key={EventParticipantIdPublic.unwrap(
                pick.entry.participantIdPublic,
              )}
              pick={pick}
              onClick={onProfileClick}
            />
          ))}
        </div>
        {hasMorePicks && (
          <GenerateMoreButton
            onClick={(): void => {
              void onGenerateMore();
            }}
            isLoading={isGeneratingMore}
          />
        )}
      </div>
    </div>
  );
}

interface RequestsTabContentProps {
  readonly requests: readonly ConnectionRequestSummary[] | undefined;
  readonly isLoading: boolean;
  readonly eventIdPublicOrSlug: EventIdPublicOrSlug;
  readonly onViewProfile: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly onShowSentRequests: () => void;
  readonly onBrowseAll: () => void;
}

function RequestsTabContent({
  requests,
  isLoading,
  eventIdPublicOrSlug,
  onViewProfile,
  onShowSentRequests,
  onBrowseAll,
}: RequestsTabContentProps): React.JSX.Element {
  if (isLoading) {
    return <DirectoryListSkeleton variant="card" showActions />;
  }

  if (requests === undefined || requests.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <RequestsInfoBanner onSentClick={onShowSentRequests} />
        <DirectoryEmptyState
          tab={DirectoryTab.REQUESTS}
          onBrowseAll={onBrowseAll}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <RequestsInfoBanner onSentClick={onShowSentRequests} />
      <div className="mt-2">
        {requests.map((request) => (
          <ConnectionRequestCardContainer
            key={ConnectionRequestIdPublic.unwrap(request.idPublic)}
            request={request}
            eventIdPublicOrSlug={eventIdPublicOrSlug}
            onViewProfile={onViewProfile}
          />
        ))}
      </div>
    </div>
  );
}

interface ConnectedTabContentProps {
  readonly profiles: readonly DirectoryCard[] | undefined;
  readonly isLoading: boolean;
  readonly onProfileClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly onBrowseAll: () => void;
}

function ConnectedTabContent({
  profiles,
  isLoading,
  onProfileClick,
  onBrowseAll,
}: ConnectedTabContentProps): React.JSX.Element {
  if (isLoading) {
    return <DirectoryListSkeleton />;
  }

  if (profiles === undefined || profiles.length === 0) {
    return (
      <DirectoryEmptyState
        tab={DirectoryTab.CONNECTED}
        onBrowseAll={onBrowseAll}
      />
    );
  }

  return (
    <ProfileList>
      {profiles.map((entry) => (
        <ProfileListItem
          key={EventParticipantIdPublic.unwrap(entry.participantIdPublic)}
          entry={entry}
          onClick={onProfileClick}
        />
      ))}
    </ProfileList>
  );
}
